import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import { useLanguage, IMG } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const SECTION_SLUGS = ["omeopatia", "neuralterapia", "medicina-naturale"];

const DETAILS = {
  it: [
    {
      intro:
        "La visita omeopatica ricostruisce la storia completa della persona: sintomi, sonno, digestione, reazioni all'ambiente, umore. Solo dopo questo quadro si sceglie il rimedio personalizzato, seguito da follow-up programmati.",
      indications: ["Disturbi ricorrenti e funzionali", "Sostenzione durante stress e convalescenze", "Rimedi personalizzati", "Follow-up regolari a distanza"],
    },
    {
      intro:
        "La neuralterapia agisce sul sistema neurovegetativo con micro-iniezioni di anestetico locale in punti definiti: cicatrici, aree riflesse, gangli. Una pratica ambulatoriale rapida e ben tollerata, sempre preceduta da un inquadramento diagnostico completo.",
      indications: ["Dolore cronico muscolo-scheletrico", "Sequele post-operatorie", "Dolori da cicatrice", "Disfunzioni neurovegetative"],
    },
    {
      intro:
        "L'alimentazione è il primo gesto di cura: percorsi alimentari consapevoli e naturali, costruiti sulla storia della persona, per favorire l'equilibrio dell'organismo. Sempre in dialogo con le terapie convenzionali, per sicurezza ed efficacia.",
      indications: ["Consulenza alimentare personalizzata", "Alimentazione consapevole e naturale", "Verifica di interazioni con farmaci", "Supporto per uno stile di vita più sano"],
    },
  ],
  en: [
    {
      intro:
        "The homeopathic consultation reconstructs the person's full story: symptoms, sleep, digestion, environmental reactions, mood. Only after this picture is the personalised remedy chosen, followed by scheduled follow-ups.",
      indications: ["Recurrent and functional disorders", "Support during stress and recovery", "Personalised remedies for each patient", "Regular follow-ups over time"],
    },
    {
      intro:
        "Neuraltherapy acts on the autonomic nervous system with micro-injections of local anaesthetic in defined points: scars, reflex areas, ganglia. A quick, well-tolerated outpatient practice, always preceded by a complete diagnostic assessment.",
      indications: ["Chronic musculoskeletal pain", "Post-surgical sequelae", "Scar-related pain", "Neurovegetative dysfunctions"],
    },
    {
      intro:
        "Nutrition is the first act of care: conscious, natural food pathways built on the person's history, to support the body's balance. Always in dialogue with conventional therapies, for safety and efficacy.",
      indications: ["Personalised nutritional counselling", "Conscious and natural eating", "Interaction checks with medicines", "Support for a healthier lifestyle"],
    },
  ],
};

export default function Trattamenti() {
  const { lang, t } = useLanguage();
  const details = DETAILS[lang];
  const images = [IMG.homeopathy, IMG.clinic, IMG.nutrition];
  const [topBySection, setTopBySection] = useState({});

  useEffect(() => {
    Promise.all(
      SECTION_SLUGS.map((s) =>
        api.get("/articles", { params: { section: s, top: 4 } }).then((res) => [s, res.data])
      )
    )
      .then((pairs) => setTopBySection(Object.fromEntries(pairs)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="trattamenti-page" className="pt-24 sm:pt-32">
      <section className="bg-white">
        <div className="container-editorial pb-16">
          <Reveal>
            <p className="eyebrow">{t.treatments.eyebrow}</p>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-slate-900 tracking-tight">
              {t.treatments.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-slate-600">
              {t.treatments.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-editorial space-y-20 sm:space-y-28">
          {t.treatments.items.map((item, i) => (
            <Reveal key={item.name}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 items-center">
                <div className={`overflow-hidden h-72 sm:h-96 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <img src={images[i]} alt={item.name} className="h-full w-full object-cover" />
                </div>
                <div className={i % 2 === 1 ? "md:order-1" : ""}>
                  <span className="font-serif italic text-5xl text-brand/25">{`0${i + 1}`}</span>
                  <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-light text-slate-900 tracking-tight">
                    {item.name}
                  </h2>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-brand">{item.tagline}</p>
                  <p className="mt-5 text-base font-light leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                  <p className="mt-3 text-sm font-light leading-relaxed text-slate-500">{details[i].intro}</p>
                  <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-brand">
                    {t.treatments.topArticles}
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {(topBySection[SECTION_SLUGS[i]] || []).map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/articoli/${a.slug}`}
                          data-testid={`treatment-article-${i}-${a.slug}`}
                          className="group flex items-start gap-2"
                        >
                          <Plus className="h-4 w-4 mt-0.5 text-brand shrink-0" />
                          <span className="text-sm font-light text-slate-700 group-hover:text-brand transition-colors duration-200">
                            {(lang === "en" && a.title_en) || a.title}
                            <span className="ml-2 text-xs text-slate-400">
                              {a.views} {t.treatments.reads}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/sezione/${SECTION_SLUGS[i]}`}
                    data-testid={`treatment-all-articles-${SECTION_SLUGS[i]}`}
                    className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-medium text-brand hover:text-brand-dark transition-colors duration-300"
                  >
                    {t.treatments.allArticles} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-linen border-y border-slate-200">
        <div className="container-editorial py-20 sm:py-24">
          <Reveal>
            <p className="eyebrow">{t.nav.trattamenti}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-light text-slate-900 tracking-tight">
              {t.about.quote}
            </h2>
            <Link
              to="/contatti"
              data-testid="trattamenti-cta-booking"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300"
            >
              {t.nav.prenota} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
